import currencyjs from 'currency.js';

export function currency(
  value: number | string,
  formatWithSymbol: boolean = true
) {
  return currencyjs(value, {
    formatWithSymbol,
    negativePattern: '!-#',
  });
}

export function formatMoney(
  value: number | string,
  options: {
    includeCents: boolean;
    formatWithSymbol?: boolean;
  } = { includeCents: true, formatWithSymbol: true }
): string {
  const out = currency(value, options.formatWithSymbol).format();

  return options.includeCents ? out : out.slice(0, out.indexOf('.'));
}

export function formatMoneyPrefixSign(
  value: number | string,
  options: {
    includeCents: boolean;
    formatWithSymbol?: boolean;
  } = { includeCents: true, formatWithSymbol: true }
) {
  const out = currencyjs(value, {
    formatWithSymbol: options.formatWithSymbol,
    pattern: '+ !#',
    negativePattern: '- !#',
  }).format();

  return options.includeCents ? out : out.slice(0, out.indexOf('.'));
}

export function formatNumberWithCommas(value: number): string {
  // https://blog.abelotech.com/posts/number-currency-formatting-javascript/
  return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

export function formatValueWithCommas(value: number | string) {
  return currencyjs(value, {
    formatWithSymbol: false,
    precision: 0,
    increment: 1,
  }).format();
}

export function formatMoneyWithNegativeParenthesis(
  value: number,
  options?: { formatWithSymbol: boolean }
) {
  return currencyjs(value, {
    negativePattern: '!(#)',
    formatWithSymbol: options?.formatWithSymbol || false,
  }).format();
}

export function formatNumberToOrdinal(value: number) {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const moddedNumber = value % 100;
  const teenSafety = (moddedNumber - 20) % 10;
  return (
    value + (suffixes[teenSafety] || suffixes[moddedNumber] || suffixes[0])
  );
}

/** Abbreviates large numbers: 1_000 → "1K", 1_530_000 → "1.5M", etc. */
export const abbreviateNumber = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${trimDecimal(value / 1e12)}T`;
  if (abs >= 1e9) return `${trimDecimal(value / 1e9)}B`;
  if (abs >= 1e6) return `${trimDecimal(value / 1e6)}M`;
  if (abs >= 1e3) return `${trimDecimal(value / 1e3)}K`;
  return String(value);
};

/** Truncates to one decimal place (no rounding): 15.39 → "15.3" */
export const trimDecimal = (num: number): string => {
  const truncated =
    num >= 0 ? Math.floor(num * 10) / 10 : Math.ceil(num * 10) / 10;
  return Number.isInteger(truncated)
    ? truncated.toString()
    : truncated.toString();
};

export const formatPrice = (value: number, decimals: number = 2): string => {
  const d = Number.isFinite(decimals)
    ? Math.max(0, Math.min(20, Math.trunc(decimals)))
    : 2;

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

  return formatter.format(value);
};

// Fees can be fractional (e.g. the $2.50 weekly installment fee); show cents only
// when needed so whole-dollar fees stay clean and fractional ones aren't rounded
// (formatPrice(x, 0) renders $2.50 as "$3").
export const formatFeeAmount = (amount: number): string =>
  formatPrice(amount, amount % 1 === 0 ? 0 : 2);

// K-suffix validation utilities for coverage limits
/**
 * Parse numeric value with optional uppercase 'K' suffix at the end only
 * Examples: '100K' → 100000, '$100K' → 100000, '100,000' → 100000, '100k' → NaN
 */
export const parseNumericValueWithK = (value: string): number => {
  if (!value) return NaN;

  const cleanValue = value.replace(/[$,]/g, '');

  // Check if it ends with exactly one uppercase 'K' and has valid numeric part before it
  if (/^\d+K$/.test(cleanValue)) {
    const numericPart = parseFloat(cleanValue.replace(/K$/, ''));
    return isNaN(numericPart) ? NaN : numericPart * 1000;
  }

  // If no 'K' or 'k' suffix, parse as regular number
  if (!/[Kk]/.test(cleanValue)) {
    return parseFloat(cleanValue);
  }

  // If contains 'K' but not in the correct format (end position, uppercase), return NaN
  return NaN;
};

/**
 * Validate coverage limit input with K character rules
 * Converts lowercase k to uppercase K early for consistent validation
 * @param value - The input value to validate
 * @param type - The coverage type: 'BI' allows slash format, 'CSL'/'PIP' are single amounts
 * @returns Object with validation result and optional error message
 */
export const validateCoverageLimitWithK = (
  value: string,
  type: 'BI' | 'CSL' | 'PIP'
): { isValid: boolean; errorMessage?: string } => {
  if (!value?.trim()) return { isValid: true }; // Empty is valid (optional)

  const allowSlash = type === 'BI';

  // Early conversion: convert k to K for consistent validation
  const normalizedValue = value.replace(/k/g, 'K');

  // Check if the normalized input contains invalid characters
  // Valid characters: digits, comma, dollar sign, uppercase K, and slash for BI
  const validPattern = allowSlash ? /^[\d,$K/]+$/ : /^[\d,$K]+$/;
  if (!validPattern.test(normalizedValue.trim())) {
    return { isValid: false, errorMessage: 'Invalid characters' };
  }

  // Clean the normalized input
  const cleanValue = cleanCoverageLimitInput(normalizedValue, allowSlash);

  if (type === 'BI') {
    // BI must contain exactly one forward slash
    const slashCount = (cleanValue.match(/\//g) || []).length;
    if (slashCount !== 1) {
      return {
        isValid: false,
        errorMessage: 'BI must have format: amount/amount',
      };
    }

    const [perPerson, perAccident] = cleanValue.split('/');

    // Both parts must exist and be valid numbers
    if (!perPerson || !perAccident) {
      return { isValid: false, errorMessage: 'Both amounts required' };
    }

    const perPersonNum = parseNumericValueWithK(perPerson);
    const perAccidentNum = parseNumericValueWithK(perAccident);

    if (
      isNaN(perPersonNum) ||
      isNaN(perAccidentNum) ||
      perPersonNum < 0 ||
      perAccidentNum < 0
    ) {
      return { isValid: false, errorMessage: 'Invalid amounts' };
    }
  } else {
    // CSL and PIP must not contain forward slash and must be single valid amounts
    // Check the original value for slash before cleaning (since cleaning removes it)
    if (value.includes('/')) {
      return {
        isValid: false,
        errorMessage: `${type} must be a single amount`,
      };
    }

    const numericValue = parseNumericValueWithK(cleanValue);

    if (isNaN(numericValue) || numericValue < 0) {
      return { isValid: false, errorMessage: 'Invalid amount' };
    }
  }

  return { isValid: true };
};

/**
 * Parse coverage limits for API requests with K-suffix support
 * Converts lowercase k to uppercase K early for consistent parsing
 * @param limitValue - The limit value string (e.g., '100K/300K' or '100k/300k')
 * @returns Object with limitPerPerson and limitPerAccident values
 */
export const parseCoverageLimitsWithK = (
  limitValue: string
): { limitPerPerson: number | null; limitPerAccident: number | null } => {
  if (!limitValue) {
    return { limitPerPerson: null, limitPerAccident: null };
  }

  // Early conversion: convert k to K for consistent parsing
  const normalizedValue = limitValue.replace(/k/g, 'K');

  // Check if the normalized input contains invalid characters before processing
  // Valid characters: digits, comma, dollar sign, uppercase K, and forward slash
  const validPattern = /^[\d,$K\\/]+$/;
  if (!validPattern.test(normalizedValue.trim())) {
    return { limitPerPerson: null, limitPerAccident: null };
  }

  // Clean the normalized input
  const cleanValue = cleanCoverageLimitInput(normalizedValue, true); // Allow slash for parsing

  if (cleanValue.includes('/')) {
    // BI format: amount/amount
    const [perPerson, perAccident] = cleanValue.split('/');
    const perPersonNum = parseNumericValueWithK(perPerson || '');
    const perAccidentNum = parseNumericValueWithK(perAccident || '');

    return {
      limitPerPerson: isNaN(perPersonNum) ? null : perPersonNum,
      limitPerAccident: isNaN(perAccidentNum) ? null : perAccidentNum,
    };
  } else {
    // CSL/PIP format: single amount
    const numericValue = parseNumericValueWithK(cleanValue);

    return {
      limitPerPerson: isNaN(numericValue) ? null : numericValue,
      limitPerAccident: null,
    };
  }
};

/**
 * Clean numeric input for coverage limits - removes invalid characters while preserving K
 * Also converts lowercase 'k' to uppercase 'K' for better user experience
 * @param value - Input value to clean
 * @param allowSlash - Whether to allow forward slash character (for BI format)
 * @returns Cleaned string with only valid characters and k converted to K
 */
export const cleanCoverageLimitInput = (
  value: string,
  allowSlash: boolean = false
): string => {
  // First convert lowercase 'k' to uppercase 'K'
  const withUppercaseK = value.replace(/k/g, 'K');

  // Then remove everything except numbers, commas, dollar signs, uppercase 'K' character, and optionally forward slashes
  const pattern = allowSlash ? /[^0-9,$/K]/g : /[^0-9,$K]/g;
  return withUppercaseK.replace(pattern, '');
};

export const isNumber = (value: unknown): boolean => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return false;
  }
  return !isNaN(Number(value));
};
